import { createTableLinkWithOpenNewTabColumn } from '@common/tableColumn.services';
import {
  Button,
  Column,
  ComponentType,
  createTableCell,
  createTableColumn,
  ModalBodyExtras,
  PaginatedTable,
  useModal,
} from '@gcl/react-core';
import { IProjectMasterScheduleOptionWithRoles } from '@masterSchedule/masterSchedule.models';
import { useGetMasterScheduleOptionsForDesignBrief } from '@masterSchedule/masterSchedule.queries';
import { useNavigate } from '@routes/routes.hooks';
import { routes } from '@routes/routes.models';
import * as React from 'react';
import { EDesignBriefDetailSearchParamKey } from './designBrief.models';

export interface IUseDesignBriefCreationModalProps {
  projectId: number;
}

export const useDesignBriefCreationModal = ({ projectId }: IUseDesignBriefCreationModalProps) => {
  const { openPortal, closePortal, renderModal } = useModal();
  const { navigate, generateRoutePath } = useNavigate();

  const { data: masterScheduleOptions } = useGetMasterScheduleOptionsForDesignBrief({ projectId });

  const masterScheduleTableColumns = React.useMemo(
    () => [
      createTableColumn({
        disableSortBy: true,
        accessor: 'id',
        Header: '',
        disableResizing: true,
        Cell: createTableCell<number, IProjectMasterScheduleOptionWithRoles>(
          (
            value: number,
            item: IProjectMasterScheduleOptionWithRoles,
            index: number,
            cellExtras: { [key: string]: any },
          ) => {
            return (
              <Button
                key={`gcl-gems-t2-creation-${index}-select`}
                className="gcl-mr-xs"
                type={ComponentType.Success}
                onClick={() => {
                  navigate(routes.t2.create.path, {}, true, undefined, {
                    [EDesignBriefDetailSearchParamKey.masterScheduleId]: item.id,
                  });
                }}
              >
                Select
              </Button>
            );
          },
        ),
      } as Column),
      createTableLinkWithOpenNewTabColumn<IProjectMasterScheduleOptionWithRoles>(
        'refNo',
        'T1 Ref No',
        ({ item }) => generateRoutePath(routes.t1.view.path, { id: item.id?.toString() }),
        ({ value, item, index, cellExtras }, link) => link,
      ),
      createTableColumn({
        Header: 'Design Package',
        accessor: 'designPackage',
        disableSortBy: true,
        disableResizing: true,
      } as Column),
    ],
    [],
  );

  const openDesignBriefCreationModal = (e: React.MouseEvent) => {
    openPortal(e);
  };

  const renderDesignBriefCreationModal = () => {
    return renderModal({
      large: true,
      title: `Select T1`,
      onRenderBody: (bodyExtras?: ModalBodyExtras) => {
        return (
          <div className="gcl-gems-design-brief-creation-form-t1-table">
            <PaginatedTable
              classNames={{ row: 'gcl-gems-design-brief-creation-form-t1-table-row' }}
              columns={masterScheduleTableColumns}
              items={masterScheduleOptions ?? []}
              disablePagination
              allowSelectRow={false}
            />
          </div>
        );
      },
      onRenderFooter: (onHide?: () => void, bodyExtras?: ModalBodyExtras) => {
        return (
          <Button
            prefixIconName="ChevronLeft"
            type={ComponentType.Secondary}
            onClick={() => {
              if (onHide) {
                onHide();
              }
            }}
            style={{ margin: '10px' }}
          >
            Cancel
          </Button>
        );
      },
    });
  };
  return {
    openPortal: openDesignBriefCreationModal,
    closePortal,
    renderModal: renderDesignBriefCreationModal,
  };
};
